import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import styled, { css } from 'styled-components';
import CategoryIcon from '@/components/category-icon';
import { CardLink, CardTitle, FeatureCard, IconTile } from '@/components/ui';
import { up } from '@/themes';

/*
 * Slugs with a photo in public/images/categories: `{slug}.avif` and
 * `{slug}.webp` (640x400, twice the largest rendered size) plus
 * `{slug}-320.avif`. Sources and photographers are in docs/image-credits.md;
 * add the slug here when a photo is added. Any other slug gets an icon panel.
 */
const PHOTO_SLUGS = new Set([
  // Gift card categories
  'clothing-and-accessories',
  'coffee',
  'entertainment',
  'flights',
  'gaming',
  'giftcard',
  'gifts-for-her',
  'gifts-for-him',
  'golf',
  'groceries',
  'health-and-spa',
  'hotels',
  'house-and-home',
  'maternity-and-toys',
  'renovations',
  'restaurants-and-food',
  'sports',
  'travel',
  'uber',
  'wine-and-cocktails',
  // Other product types (country pages list them beside the categories)
  'bundle',
  'data',
  'digitaltv',
  'esim',
  'longdistance',
  'pin',
  'topup',
]);

const PHOTO_WIDTH = 640;
const PHOTO_HEIGHT = 400;

/*
 * Gift card categories also have a larger 4:3 crop of the same photo for the
 * top of their /shop/{slug} page: `{slug}-hero.avif` (1200x900),
 * `{slug}-hero-720.avif` (720x540) and `{slug}-hero.webp` (960x720).
 */
const NO_HERO_SLUGS = new Set([
  'giftcard',
  'bundle',
  'data',
  'digitaltv',
  'esim',
  'longdistance',
  'pin',
  'topup',
]);

/** Path of a category's photo without its extension, or null when it has none. */
export const photoPath = (slug) => {
  const name = slug === 'giftcards' ? 'giftcard' : slug;

  return PHOTO_SLUGS.has(name) ? `/images/categories/${name}` : null;
};

/** Path of a category's hero photo without its extension, or null when it has none. */
export const heroPhotoPath = (slug) =>
  PHOTO_SLUGS.has(slug) && !NO_HERO_SLUGS.has(slug) ? `/images/categories/${slug}-hero` : null;

/*
 * Rendered width of a card: 2 columns with a 12px gap on phones, 3 columns
 * from 640px, `columns` from 1024px, inside the 1280px container.
 */
const photoSizes = (columns) => {
  const gaps = (columns - 1) * 16;

  return [
    `(min-width: 1280px) ${Math.round((1216 - gaps) / columns)}px`,
    `(min-width: 1024px) calc((100vw - ${64 + gaps}px) / ${columns})`,
    '(min-width: 640px) calc((100vw - 80px) / 3)',
    'calc((100vw - 44px) / 2)',
  ].join(', ');
};

const Grid = styled.ul`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  list-style: none;
  margin: 0;
  padding: 0;

  ${up.sm} {
    gap: 16px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  ${up.lg} {
    grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  }
`;

/* A compact row on phones (icon beside the name), a stacked card from 640px. */
const Tile = styled(FeatureCard)`
  align-items: center;
  flex-direction: row;
  gap: 10px;
  padding: 12px;

  ${up.sm} {
    align-items: flex-start;
    flex-direction: column;
    gap: 16px;
    padding: 20px;
  }
`;

const Name = styled(CardTitle)`
  font-size: 14px;
  line-height: 20px;
  min-width: 0;
  overflow-wrap: break-word;

  ${up.sm} {
    font-size: 16px;
    line-height: 24px;
  }
`;

/*
 * The marketing photo card: a 16:10 photo, then the icon beside the name.
 * `isolation` keeps the zooming photo inside the rounded corners in Safari.
 */
const PhotoCard = styled(FeatureCard)`
  isolation: isolate;
  padding: 0;
`;

const Photo = styled.div`
  aspect-ratio: 16 / 10;
  background-color: var(--muted);
  flex-shrink: 0;
  overflow: hidden;

  picture {
    display: block;
    height: 100%;
  }

  img {
    display: block;
    height: 100%;
    object-fit: cover;
    transition: transform 500ms ease-in-out;
    width: 100%;
  }

  ${PhotoCard}:hover & img,
  ${PhotoCard}:focus-within & img {
    transform: scale(1.05);
  }

  @media (prefers-reduced-motion: reduce) {
    img {
      transition: none;
    }

    ${PhotoCard}:hover & img,
    ${PhotoCard}:focus-within & img {
      transform: none;
    }
  }
`;

/* Stands in for the photo of a category that has none, so every card in a row has the same shape. */
const IconPanel = styled(Photo)`
  align-items: center;
  background-color: color-mix(in oklab, var(--primary) 8%, var(--muted));
  color: color-mix(in oklab, var(--primary) 70%, transparent);
  display: flex;
  justify-content: center;
`;

/* 28px on phones, where a card is about 160px wide; 32px from 640px. */
const BodyIcon = styled(IconTile)`
  height: 28px;
  width: 28px;

  ${up.sm} {
    height: 32px;
    width: 32px;
  }
`;

const Body = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  gap: 8px;
  padding: 12px;

  ${up.sm} {
    gap: 10px;
    padding: 20px;
  }

  /*
   * Five or more cards in a row are 150 to 190px wide: tighter padding and
   * smaller type, and the icon only once the name has room beside it.
   */
  ${({ $dense }) =>
    $dense &&
    css`
      ${up.lg} {
        gap: 8px;
        padding: 12px;

        ${BodyIcon} {
          display: none;
        }

        ${Name} {
          font-size: 14px;
          line-height: 20px;
        }
      }

      ${up.xl} {
        padding: 14px;

        ${BodyIcon} {
          display: inline-flex;
        }
      }
    `};
`;

/**
 * One category as an interactive card. Links to the category's shop page
 * unless the category carries its own `href`; the name comes from the
 * `categories` messages unless it carries its own `label`.
 *
 * `variant="photo"` (default) is the photo card: the category's photo on top,
 * or an icon panel when it has none. `variant="compact"` is the icon-only
 * card. `sizes` is the photo's `sizes` attribute; `dense` tightens the photo
 * card for rows of five or more.
 */
export const Category = ({ category, dense, sizes, variant = 'photo' }) => {
  const t = useTranslations('categories');

  const link = (
    <CardLink as={Link} href={category.href || `/shop/${category.slug}`}>
      {category.label || t(category.slug)}
    </CardLink>
  );

  if (variant === 'compact') {
    return (
      <Tile $interactive>
        <IconTile>
          <CategoryIcon size={20} slug={category.slug} />
        </IconTile>
        <Name>{link}</Name>
      </Tile>
    );
  }

  const photo = photoPath(category.slug);

  return (
    <PhotoCard $interactive>
      {photo ? (
        <Photo>
          {/* Decorative: the name is right below. The files are already sized, so no optimizer. */}
          <picture>
            <source
              sizes={sizes}
              srcSet={`${photo}-320.avif 320w, ${photo}.avif ${PHOTO_WIDTH}w`}
              type="image/avif"
            />
            <Image
              alt=""
              height={PHOTO_HEIGHT}
              loading="lazy"
              src={`${photo}.webp`}
              unoptimized
              width={PHOTO_WIDTH}
            />
          </picture>
        </Photo>
      ) : (
        <IconPanel>
          <CategoryIcon size={40} slug={category.slug} />
        </IconPanel>
      )}
      <Body $dense={dense}>
        <BodyIcon $size="sm">
          <CategoryIcon size={16} slug={category.slug} />
        </BodyIcon>
        <Name>{link}</Name>
      </Body>
    </PhotoCard>
  );
};

/**
 * Grid of category cards: 2 columns on phones, 3 from 640px and `columns`
 * from 1024px.
 *
 *   <CategoriesGrid categories={categories} />
 *   <CategoriesGrid categories={categories} variant="compact" />
 *
 * `categories`: `[{ id, slug, href?, label? }]`.
 * `variant`: `photo` (default; photo cards, 4 columns) or `compact`
 * (icon-only cards, 6 columns). Photo cards also work six to a row
 * (`columns={6}`).
 */
const CategoriesGrid = ({ categories = [], className, columns, variant = 'photo' }) => {
  const columnCount = columns || (variant === 'compact' ? 6 : 4);
  const sizes = photoSizes(columnCount);

  return (
    <Grid $columns={columnCount} className={className}>
      {categories.map((category) => (
        <li key={category.id || category.slug}>
          <Category category={category} dense={columnCount >= 5} sizes={sizes} variant={variant} />
        </li>
      ))}
    </Grid>
  );
};

export default CategoriesGrid;
